/**
 * SSOT: supplier code lookup for LDS pricing / commission matching (parity with BE
 * services.lds_supplier_code_resolve.resolve_lds_supplier_code_for_pricing).
 *
 * @param {Record<string, unknown>|null|undefined} deal
 * @returns {string|null}
 */
export function resolveLdsSupplierCodeForPricing(deal) {
  if (!deal || typeof deal !== "object") {
    return null;
  }
  /** @type {Record<string, unknown>} */
  const d = deal;
  const sd = d.supplierDetails && typeof d.supplierDetails === "object" ? d.supplierDetails : {};

  const pick = (...vals) => {
    for (const v of vals) {
      const s = v != null ? String(v).trim() : "";
      if (s !== "") return s;
    }
    return null;
  };

  let code =
    pick(sd.supplierId, sd.supplierCode, sd.code, sd.supplier_code, sd.id);
  if (code) return _logResolved(deal, code, "supplierDetails");

  const lcc = d.ldsCommissionCondition;
  const fromLcc =
    lcc && typeof lcc === "object"
      ? pick(lcc.supplier_code)
      : null;
  if (fromLcc) return _logResolved(deal, fromLcc, "ldsCommissionCondition.supplier_code");

  const snaps = [];
  const psTop = d.pricingSnapshot;
  if (psTop && typeof psTop === "object") snaps.push(psTop);
  const our = d.ourPrice;
  const psNested = our && typeof our === "object" ? our.pricingSnapshot : null;
  if (psNested && typeof psNested === "object") snaps.push(psNested);

  for (const snap of snaps) {
    const sc = pick(snap.supplier_code);
    if (sc) return _logResolved(deal, sc, "pricingSnapshot.supplier_code");
    const rule = snap.supplier_rule;
    if (rule && typeof rule === "object") {
      const sr = pick(rule.supplier_code);
      if (sr) return _logResolved(deal, sr, "pricingSnapshot.supplier_rule.supplier_code");
    }
  }

  return null;
}

function _organizedTourHint(d) {
  const dtRaw = String(
    d.dealType ??
      d.dealtype ??
      d.deal_type ??
      ""
  )
    .toUpperCase()
    .replace(/[\s_/&+-]/g, "");
  if (
    dtRaw.includes("ORGANIZEDTOUR") ||
    dtRaw === "ORGANIZEDTOURS" ||
    dtRaw === "ORGANIZED"
  )
    return true;
  const sub = String(
    (d.packageSubType ??
      d.package_sub_type ??
      d.dealSubType ??
      d.deal_subtype ??
      "") +
      ""
  )
    .toUpperCase()
    .replace(/\s+/g, "");
  if (
    sub === "ORGANIZEDTOUR" ||
    sub === "ORGANIZEDTOURS" ||
    sub === "ORGANIZED"
  )
    return true;
  const tour = d.tour;
  const hotel = d.hotel;
  const hasTour =
    tour && typeof tour === "object" && Object.keys(tour).length > 0;
  const hasHotel =
    Boolean(hotel) &&
    !(typeof hotel === "object" && Object.keys(hotel).length === 0);
  return Boolean(hasTour && !hasHotel);
}

/** @param {Record<string, unknown>} dealHint @param {string} code @param {string} path */
function _logResolved(dealHint, code, path) {
  const hint = _organizedTourHint(dealHint);
  const payload = { resolved_code: code, path, organized_tour_hint: hint };
  if (typeof console !== "undefined" && console.info) {
    console.info("[LDS_SUPPLIER_CODE_RESOLVE]", JSON.stringify(payload));
    if (hint) {
      console.info("[ORG_TOUR_SUPPLIER_CODE_RESOLVE]", JSON.stringify(payload));
      console.info("[ORG_TOUR_TRACE_SUPPLIER_CODE_DISPLAY]", JSON.stringify(payload));
    }
  }
  return code;
}
